import { getApiClient } from "@src/api";
import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import type { WhiteboardDocumentId } from "@src/components/collaboration/document-id";
import { graphql } from "@src/gql/gql";
import {
	NavBarItemType,
	NavbarSection,
	WhiteboardStatus,
} from "@src/gql/graphql";
import { handleError } from "@src/utils/error-handling";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// Define the whiteboard creation mutation
export const createWhiteboardMutation = graphql(/* GraphQL */ `
	mutation createWhiteboard($name: String!, $id: String, $correlationId: String) {
		whiteboards {
			create(name: $name, whiteboardId: $id, correlationId: $correlationId) {
				whiteboard {
					name
					ownerId
					status
				}
				id
			}
		}
	}
`);

export const useCreateWhiteboard = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			id,
			name,
		}: { id: WhiteboardDocumentId; name: string }) => {
			const whiteboardResult = await client.mutate({
				mutation: createWhiteboardMutation,
				variables: {
					id: id.toIdString(),
					name,
				},
			});

			const whiteboardId = whiteboardResult.data?.whiteboards?.create?.id;

			if (!whiteboardId) {
				throw new Error("Failed to create whiteboard");
			}

			return {
				data: whiteboardResult.data?.whiteboards.create,
				id: whiteboardId,
			};
		},
		onSuccess: (result) => {
			const section = NavbarSection.MyWhiteboards;
			const queryKey = navbarBranchQueryKeys.branch(section, null);

			queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (oldData) => {
				const newBoardItem: NavbarBranchItem = {
					key: result.id,
					name: result.data?.whiteboard?.name || "Untitled",
					type: NavBarItemType.Board,
					parentKey: null,
					rank: "",
					hasChildren: false,
					status: result.data?.whiteboard?.status || WhiteboardStatus.Active,
				};
				return [newBoardItem, ...(oldData ?? [])];
			});

			// Revalidate in the background
			queryClient.invalidateQueries({ queryKey });
		},
		onError: (error) => {
			handleError(error, "Failed to create whiteboard");
		},
	});
};
